/* eslint-disable jsx-a11y/no-autofocus */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
} from '../../../../../utilities/sharedQueries';
import { useRate } from '../../../../../utilities/sharedHooks/rates';
import { useCurrency } from '../../../../../utilities/sharedHooks/settings';
import {
  useLedgerAccountForWallet,
  useLedgerFilteredCurrencies,
} from '../../../../../utilities/sharedHooks/ledgerWallets';
import { currencies, exchange } from '../../../../../utilities/constants';
import { useLedgerTransaction } from '../../../../../utilities/sharedHooks/ledgerSDKHooks';
import {
  AMOUNT_INPUT_MAX_LENGTH,
  NETWORK_FEES_TYPES,
  WalletScreens,
} from '../../../../Accounts/constants';
import {
  formatCryptoAmountOnBlur,
  formatDecimalNumber,
  formatNumber,
} from '../../../../../utilities/formatHelpers';
import { validateNumbers } from '../../../../../utilities/formValidation';
import { orderCardFlowScreens, WalletsTypes } from '../../../constants';
import Alert from '../../../../../components/Alert';
import { Button, Typography } from '../../../../../components';
import ModalBodyLayout from '../partials/MainBodyLayout';
import FocusableInputArea from '../../../../Accounts/partials/FocusableInputArea';

const TopUpWallet = () => {
  const { t: translate } = useTranslation();

  const [fiatInputWidth, setFiatInputWidth] = useState(0);
  const [cryptoInputWidth, setCryptoInputWidth] = useState(0);
  const [payCrypto, setPayCrypto] = useState(true);
  const [isSwap, setIsSwap] = useState(false);
  const fiatInputSpan = useRef();
  const cryptoInputSpan = useRef();
  const textInputRef = useRef(null);
  const [form, setForm] = useState({
    fiat: '0.00',
    crypto: '0',
    amount: 0,
    fee: '',
  });
  const [networkFeeType, setNetworkFeeType] = useState(NETWORK_FEES_TYPES.Medium);
  const { data: userPreferences } = useQuery(USER_PREFERENCES);
  const { displayCurrency } = userPreferences || {};
  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const { state, openScreen, updatePaymentInfo } = useContext(OrderCardContext);

  const screenOptions = state?.screenOptions;
  const featurePgwAllowed = state?.cardOrderSettings?.app?.feature_pgw_allowed;
  const orderCardFreeLimitBalance = state?.cardOrderSettings?.server?.promo_amount_free_card;
  const orderCardFreeLimitBalanceCurrency =
    state?.cardOrderSettings?.server?.promo_amount_free_card_currency;
  const payCardFundCurrencies = state?.cardOrderSettings?.server?.stable_coin_can_fund_by_card;
  const cardOrderFee = state?.cardOrderFee;

  const wallet = screenOptions?.wallet;
  const ledgerAccount = useLedgerAccountForWallet(wallet);

  const rate = useRate(wallet?.coin, displayCurrency, true);

  const currency = useCurrency(wallet?.coin?.toLowerCase());
  const { sendTransaction, isLoading: isLoadingTransaction } = useLedgerTransaction(
    wallet,
    currency
  );
  const isLoading = isLoadingTransaction;

  const isAllowBuyPGW = useMemo(() => {
    if (supportedCurrencies && wallet?.coin) {
      return supportedCurrencies[wallet?.coin]
        ? supportedCurrencies[wallet?.coin].allows_buy_pgw
        : false;
    }
    return false;
  }, [supportedCurrencies, wallet?.coin]);

  const cardOrderFeeForDisplay = useMemo(
    () => {
      const cardOrderFeeValue = cardOrderFee / 100;
      const coinCurrency = state?.cardCurrency?.coin || displayCurrency;
      return `${currencies[coinCurrency].symbol}${cardOrderFeeValue}`;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardOrderFee, state?.cardCurrency?.coin]
  );

  // TODO: below two useEffects must be refactored
  useEffect(() => {
    if (fiatInputSpan.current) {
      const charactersLength =
        fiatInputSpan.current.value.length > 1 ? fiatInputSpan.current.value.length : 1;
      // to avoid having empty input, fix length to 1 if text length is less then 1
      setFiatInputWidth(charactersLength);
    }
  }, [form.fiat]);

  useEffect(() => {
    if (cryptoInputSpan.current) {
      const closestContainer = cryptoInputSpan.current.closest('.d-flex');
      const closestContainerParent = closestContainer.closest('.modal-body');
      const charactersLength =
        cryptoInputSpan.current.value.length > 1 ? cryptoInputSpan.current.value.length : 2;
      // to avoid having empty input, fix length to 2 if text length is less then 1
      if (closestContainerParent?.offsetWidth < closestContainer.scrollWidth) {
        closestContainer.style.fontSize = '1.5rem';
      } else if (cryptoInputSpan.current.value.length < AMOUNT_INPUT_MAX_LENGTH) {
        // resize input font size to 2.125rem when char length is less then AMOUNT_INPUT_MAX_LENGTH
        closestContainer.style.fontSize = '2.125rem';
      }
      setCryptoInputWidth(charactersLength);
    }
  }, [form.crypto]);

  const handleChangeFiat = (e) => {
    const { value, min } = e.target;

    const { decimalAmount, formattedAmount } = formatDecimalNumber(value.toString(), min);

    if (!validateNumbers(decimalAmount, WalletsTypes.FIAT)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setForm({
        ...form,
        amount: 0,
        fiat: '0.00',
        crypto: '0',
      });
    }

    const calcCrypto = formatNumber(decimalAmount / rate, exchange.CRYPTO_VALUE);
    const formattedCalcCrypto = formatDecimalNumber(calcCrypto);

    setForm({
      ...form,
      amount: Number(
        parseFloat(formattedCalcCrypto.decimalAmount).toFixed(currency.currency_exponent)
      ),
      fiat: formattedAmount,
      crypto: calcCrypto === Infinity ? '-' : formattedCalcCrypto.formattedAmount,
    });
  };

  const handleChangeCrypto = (e) => {
    const { value, min } = e.target;

    const { decimalAmount, formattedAmount } = formatDecimalNumber(value.toString(), min);

    if (!validateNumbers(decimalAmount, WalletsTypes.CRYPTO)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setForm({
        ...form,
        amount: 0,
        fiat: '0.00',
        crypto: '0',
      });
    }

    const calcFiat = formatNumber(decimalAmount * rate, exchange.FIAT_VALUE);
    const formattedFiatCalc = formatDecimalNumber(calcFiat);

    setForm({
      ...form,
      amount: Number(parseFloat(decimalAmount).toFixed(currency.currency_exponent)),
      crypto: formattedAmount,
      fiat: formattedFiatCalc.formattedAmount,
    });
  };

  const handleFiatFocus = (e) => {
    if (!e.target) return;
    e.target.select();
  };

  const handleFocus = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
    if (e.target.name === 'fee') {
      textInputRef.current.style.display = 'inline-block';
    }
  };

  const handleBlur = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
    if (e.target.name === 'fee' && e.target.value.trim() === '') {
      textInputRef.current.style.display = 'none';
    }

    const decimalNumber = formatCryptoAmountOnBlur(e?.target?.value);
    if (decimalNumber) {
      setForm({
        ...form,
        crypto: decimalNumber.valueWithDecimal,
      });
      // to avoid having empty input, fix length to 1 if text length is less then 1
      setCryptoInputWidth(decimalNumber.charactersLength);
    }
  };

  const onSubmitTransaction = async () => {
    const { success } = await sendTransaction(form.amount, networkFeeType);
    updatePaymentInfo('type', WalletsTypes.CRYPTO);
    if (success) {
      openScreen(orderCardFlowScreens.TRANSACTION_PROCESSED, { ...screenOptions });
    }
  };

  const renderFooter = () => (
    <div className="mt-4 pt-3">
      {screenOptions && screenOptions?.isCardOrderFlow && (
        <Alert icon="info" type="info">
          {translate('add_funds_flow_alert')}
        </Alert>
      )}
      {!process.env.REACT_APP_FLAVOR && screenOptions && !screenOptions?.isCardOrderFlow && (
        <Alert icon="info" type="info">
          {translate('receive_with_ledger_note')}
        </Alert>
      )}
      <div className="btn-container">
        {!process.env.REACT_APP_FLAVOR && (
          <Button
            variant="primary"
            className="w-100"
            disabled={!(ledgerAccount && form?.amount > 0)}
            loading={isLoading ? 'true' : 'false'}
            onClick={onSubmitTransaction}>
            {translate('continue_with_ledger_btn_label')}
          </Button>
        )}
        <Button
          variant="primarySimple"
          className="mt-4 w-100"
          // todo: update the action and the screen
          onClick={() => openScreen(WalletScreens.RECEIVE, { ...screenOptions })}>
          {translate('receive_from_external_address_label')}
        </Button>
      </div>
    </div>
  );

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() => openScreen(orderCardFlowScreens.SELECT_PAYMENT, { ...screenOptions })}
      title={translate('card_order_top_up_wallet_title').replace('%s', wallet?.coin.toUpperCase())}>
      <div className="pt-4">
        <Typography variant="baseSemi">
          {translate('top_up_balance_free_card_1')}{' '}
          <Typography
            as="span"
            variant="baseSemi"
            className="bg-attention-info d-inline-block text-center rounded-2 py-0 px-1">
            {currencies[orderCardFreeLimitBalanceCurrency].symbol}
            {formatNumber(orderCardFreeLimitBalance, exchange.FIAT_VALUE)}
          </Typography>{' '}
          {translate('top_up_balance_free_card_2')}{' '}
          <Typography as="span" variant="baseBold">
            {cardOrderFeeForDisplay}
          </Typography>{' '}
          {translate('top_up_balance_free_card_3')}
        </Typography>
        <Typography variant="small" className="text-shadow w-85 mt-3">
          {translate('top_up_balance_free_card_4')}
        </Typography>
      </div>
      <div className="d-flex flex-column h-100 mt-5">
        {payCardFundCurrencies &&
          payCardFundCurrencies[0].split(',').includes(wallet?.coin.toLocaleLowerCase()) && (
            <div className="w-100 rounded pb-1 pt-2">
              <div className="btn-light w-100 d-flex flex-row-center bg-lighter border border-disable rounded-4">
                <Button
                  variant="toggleItem"
                  className={`text-white p-1 w-50 rounded-3 ${payCrypto && 'active'}`}
                  onClick={() => setPayCrypto(true)}>
                  {translate('crypto_payment')}
                </Button>
                <Button
                  variant="toggleItem"
                  disabled={!isAllowBuyPGW || !featurePgwAllowed}
                  className={`text-white p-1 w-50 rounded-3 ${payCrypto && 'active'}`}
                  onClick={() => setPayCrypto(true)}>
                  {translate('payment_card')}
                </Button>
              </div>
            </div>
          )}
        <div className="text-center d-flex align-items-center">
          <div className="w-100 h-100">
            <FocusableInputArea>
              <input
                required
                min="0"
                type="text"
                className="form-control bg-transparent p-0 text-center border-0"
                name="fiat"
                value={isSwap ? form.crypto : form.fiat}
                style={{
                  width: `${(isSwap ? cryptoInputWidth : fiatInputWidth) + 1}ch`,
                  height: '1.5rem',
                }}
                ref={isSwap ? cryptoInputSpan : fiatInputSpan}
                onChange={(e) => {
                  if (isSwap) {
                    handleChangeCrypto(e);
                  } else {
                    handleChangeFiat(e);
                  }
                }}
                onFocus={handleFiatFocus}
                disabled
                placeholder="0 "
              />
              <span style={{ fontSize: 'inherit', pointerEvents: 'none' }} className="text-f5">
                {isSwap ? wallet?.coin.toUpperCase() : displayCurrency}
              </span>
              <span
                role="button"
                className="mb-1"
                tabIndex="0"
                onKeyPress={() => setIsSwap((a) => !a)}
                onClick={() => setIsSwap((a) => !a)}>
                <img src="./assets/images/switch.svg" className="ms-2" alt="switch" />
              </span>
            </FocusableInputArea>
            <FocusableInputArea className="h1 amount-input">
              <input
                required
                autoFocus
                min="0"
                type="text"
                className="form-control bg-transparent text-bold p-0 text-center border-0"
                value={isSwap ? form.fiat : form.crypto}
                style={{
                  fontSize: 'inherit',
                  width: `${(isSwap ? fiatInputWidth : cryptoInputWidth) + 1}ch`,
                  color: 'inherit',
                  height: 'inherit',
                }}
                ref={isSwap ? fiatInputSpan : cryptoInputSpan}
                onChange={(e) => {
                  if (isSwap) {
                    handleChangeFiat(e);
                  } else {
                    handleChangeCrypto(e);
                  }
                }}
                onFocus={handleFocus}
                onBlur={!isSwap ? handleBlur : () => {}}
                placeholder="0  "
              />
              <b
                style={{
                  fontSize: 'inherit',
                  pointerEvents: 'none',
                  color: 'inherit',
                }}>
                {isSwap ? displayCurrency : wallet?.coin.toUpperCase()}
              </b>
            </FocusableInputArea>
            {payCrypto && (
              <>
                <Typography variant="small" className="text-shadow text-start pt-3 mt-4 pb-2">
                  {translate('deposit_wallet_select_fees')}
                </Typography>
                <div className="flex-row-center bg-lighter border border-disabled rounded-4">
                  <Button
                    style={{ height: '2rem' }}
                    variant="toggleItem"
                    className={`${
                      networkFeeType === NETWORK_FEES_TYPES.Slow && 'active'
                    } btn-sm flex-fill p-1 small-badge`}
                    onClick={() => setNetworkFeeType(NETWORK_FEES_TYPES.Slow)}>
                    {translate('deposit_wallet_low')}
                  </Button>
                  <Button
                    style={{ height: '2rem' }}
                    variant="toggleItem"
                    className={`${
                      networkFeeType === NETWORK_FEES_TYPES.Medium && 'active'
                    } btn-sm flex-fill p-1 small-badge`}
                    onClick={() => setNetworkFeeType(NETWORK_FEES_TYPES.Medium)}>
                    {translate('deposit_wallet_standard')}
                  </Button>
                  <Button
                    style={{ height: '2rem' }}
                    variant="toggleItem"
                    className={`${
                      networkFeeType === NETWORK_FEES_TYPES.Fast && 'active'
                    } btn-sm flex-fill p-1 small-badge`}
                    onClick={() => setNetworkFeeType(NETWORK_FEES_TYPES.Fast)}>
                    {translate('deposit_wallet_high')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {renderFooter()}
    </ModalBodyLayout>
  );
};

export default TopUpWallet;
